import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { DateValidationError } from '@mui/x-date-pickers/models';
import type { PickerChangeHandlerContext } from '@mui/x-date-pickers/models/pickers';
import { he } from 'date-fns/locale';
import type { FieldAppearance } from '../fieldAppearance';
import { SmPickerField, type SmPickerFieldProps } from '../shared/SmPickerField';
import { formatPickerChange, toPickerDate } from './utils';

const DEFAULT_FORMAT = 'dd/MM/yyyy';

export type DatePickerBaseProps = {
  label?: string;
  /** @default 'default' */
  appearance?: FieldAppearance;
  placeholder?: string;
  /** @default true */
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  minDate?: Date;
  maxDate?: Date;
  format?: string;
};

type StringDatePickerProps = DatePickerBaseProps & {
  value: string;
  onChange: (value: string | null) => void;
};

type DateDatePickerProps = DatePickerBaseProps & {
  value: Date | null;
  onChange: (value: Date | null) => void;
};

export type DatePickerProps = StringDatePickerProps | DateDatePickerProps;

function DatePickerImpl({
  label,
  appearance = 'default',
  placeholder,
  fullWidth = true,
  disabled,
  error,
  helperText,
  minDate,
  maxDate,
  format = DEFAULT_FORMAT,
  value,
  onChange
}: DatePickerProps) {
  const updateValue = (date: Date | null) => {
    if (typeof value === 'string') {
      (onChange as StringDatePickerProps['onChange'])(formatPickerChange(value, date));
    } else {
      (onChange as DateDatePickerProps['onChange'])(formatPickerChange(value, date));
    }
  };

  const handleChange = (date: Date | null, context: PickerChangeHandlerContext<DateValidationError>) => {
    if (context.validationError == null) {
      updateValue(date);
    }
  };

  const textFieldProps = {
    label,
    fullWidth,
    error,
    helperText,
    appearance,
    placeholder,
    endIcon: <CalendarTodayOutlinedIcon />
  } as SmPickerFieldProps;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <MuiDatePicker
        label={label}
        value={toPickerDate(value)}
        onChange={handleChange}
        onAccept={updateValue}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        format={format}
        slotProps={{ textField: textFieldProps }}
        slots={{ textField: SmPickerField }}
      />
    </LocalizationProvider>
  );
}

export function DatePicker(props: StringDatePickerProps): JSX.Element;
export function DatePicker(props: DateDatePickerProps): JSX.Element;
export function DatePicker(props: DatePickerProps): JSX.Element {
  return DatePickerImpl(props);
}
